import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, BarChart2, Users, Plus, Settings, Building2, TrendingUp, TrendingDown, Box, FileText, Camera, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import useSubscription from "@/contexts/SubscriptionContext";
import { canUploadImages } from "@/lib/featureGates";
import ProgressQuickEntryModal from "@/components/progress/ProgressQuickEntryModal";

const BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { status: subscriptionStatus } = useSubscription();
  const tierBlocked = !canUploadImages(subscriptionStatus);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const path = location.pathname;
  const [iOSDevice, setIOSDevice] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const fabRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Check if the device is iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIOSDevice(isIOS);
  }, []);

  useEffect(() => {
    setFabOpen(false);
  }, [path]);

  useEffect(() => {
    if (!fabOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!fabRef.current) return;
      if (!fabRef.current.contains(event.target as Node)) {
        setFabOpen(false);
      }
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFabOpen(false);
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onEscape);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onEscape);
    };
  }, [fabOpen]);

  const isActive = (route: string) => route === "/" ? path === "/" : path.startsWith(route);

  const currentProjectId = useMemo(() => {
    const match = path.match(/^\/project\/([^/]+)/);
    return match?.[1] ?? null;
  }, [path]);

  const buildEntryUrl = (type: "expense" | "receivedCheck" | "delivery" | "sitePhoto") => {
    const params = new URLSearchParams({ type });
    if (currentProjectId) params.set("projectId", currentProjectId);
    return `/new-entry?${params.toString()}`;
  };

  const quickActions = [
    {
      key: "new-project",
      label: "Yeni Proje",
      Icon: Building2,
      iconBg: "bg-brand-100",
      iconColor: "text-brand-600",
      onClick: () => navigate("/new-project"),
    },
    {
      key: "income-entry",
      label: "Gelir Kaydı",
      Icon: TrendingUp,
      iconBg: "bg-success-50",
      iconColor: "text-success-600",
      onClick: () => navigate(currentProjectId ? `/salepage?projectId=${currentProjectId}` : "/salepage"),
    },
    {
      key: "expense-entry",
      label: "Masraf Kaydı",
      Icon: TrendingDown,
      iconBg: "bg-error-50",
      iconColor: "text-error-600",
      onClick: () => navigate(buildEntryUrl("expense"), { state: { from: path, quickAction: true } }),
    },
    {
      key: "material-entry",
      label: "Malzeme Girişi",
      Icon: Box,
      iconBg: "bg-warning-50",
      iconColor: "text-warning-600",
      onClick: () => navigate(buildEntryUrl("delivery"), { state: { from: path, quickAction: true } }),
    },
    {
      key: "delivery-entry",
      label: "İrsaliye Ekle",
      Icon: FileText,
      iconBg: "bg-gray-100",
      iconColor: "text-gray-600",
      onClick: () => navigate(buildEntryUrl("delivery"), { state: { from: path, quickAction: true } }),
    },
    {
      key: "site-photo-entry",
      label: "Fotoğraf Ekle",
      Icon: Camera,
      iconBg: "bg-pink-50",
      iconColor: "text-pink-600",
      onClick: () => {
        // A site-photo entry requires a photo, so free-tier users have nothing to do here
        if (tierBlocked) {
          toast.info(t("photo_upload.premium_required"));
          navigate("/subscription");
          return;
        }
        navigate(buildEntryUrl("sitePhoto"), { state: { from: path, quickAction: true } });
      },
    },
    {
      key: "progress-entry",
      label: "İlerleme Kaydet",
      Icon: ListChecks,
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-600",
      onClick: () => setProgressModalOpen(true),
    },
  ];

  // Handler for the new entry button
  const handleNewEntryClick = () => {
    // Check if we're already on the new entry page
    if (path.includes('/new-entry')) {
      // If already on the new entry page, we want to reset to entry selection
      // Dispatch a custom event that EntryTypeModal will listen for
      window.dispatchEvent(new CustomEvent('reset-entry-selection'));
    } else {
      setFabOpen((prev) => !prev);
    }
  };

  const navItems = [
    { key: "home", route: "/", label: t("navigation.home"), Icon: Home },
    { key: "people", route: "/people", label: t("navigation.people"), Icon: Users },
    { key: "reports", route: "/reports", label: t("navigation.reports"), Icon: BarChart2 },
    { key: "settings", route: "/settings", label: t("settings.title"), Icon: Settings },
  ];

  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-[999] h-[72px] border-t border-gray-200 bg-white shadow-[0_-2px_3.4px_rgba(10,13,18,0.06)]",
      iOSDevice && "ios-safe-area-bottom"
    )}>
      <div className="relative mx-auto grid h-full w-full max-w-[420px] grid-cols-5 items-center px-3">
        {navItems.slice(0, 2).map(({ key, route, label, Icon }) => {
          const active = isActive(route);
          return (
            <button
              key={key}
              onClick={() => navigate(route)}
              className={cn(
                "relative flex h-full flex-col items-center justify-center gap-1 px-1",
                active ? "text-brand-600" : "text-[#3B608F]"
              )}
            >
              {active && (
                <span className="absolute left-1/2 top-1 h-1 w-8 -translate-x-1/2 rounded-full bg-brand-600" />
              )}
              <Icon className="h-5 w-5" />
              <span className={cn("text-[11px] leading-[16.5px]", active && "font-semibold")}>
                {label}
              </span>
            </button>
          );
        })}

        <div ref={fabRef} className="relative flex h-full items-center justify-center">
          <div
            className={cn(
              "absolute bottom-[74px] left-1/2 z-[1003] w-[220px] -translate-x-1/2 rounded-2xl border border-white bg-white p-3 pb-5 shadow-[0_25px_25px_rgba(0,0,0,0.25)] transition-all duration-220 ease-out",
              fabOpen
                ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                : "pointer-events-none translate-y-2 scale-95 opacity-0"
            )}
            aria-hidden={!fabOpen}
          >
            <div className="space-y-1">
              {quickActions.map(({ key, label, Icon, iconBg, iconColor, onClick }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setFabOpen(false);
                    onClick();
                  }}
                  className={cn(
                    "flex h-[60px] w-full items-center gap-3 rounded-xl px-4 text-left transition-colors",
                    key === "new-project" ? "bg-brand-100" : "hover:bg-gray-50"
                  )}
                >
                  <span className={cn("inline-flex h-9 w-9 items-center justify-center rounded-lg", iconBg)}>
                    <Icon className={cn("h-4 w-4", iconColor)} />
                  </span>
                  <span className="text-sm font-semibold text-brand-900">{label}</span>
                </button>
              ))}
            </div>
            <div className="absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-b border-r border-white bg-white" />
          </div>

          <button
            onClick={handleNewEntryClick}
            className="absolute -top-[22px] z-[1002] flex h-[56px] w-[56px] items-center justify-center rounded-full border border-brand-600 bg-brand-600 text-white shadow-[0_8.8px_5.5px_rgba(0,0,0,0.1),0_22px_13.75px_rgba(0,0,0,0.1)]"
            style={{ backgroundImage: "linear-gradient(135deg, #1565C0 0%, #0C4A8A 100%)" }}
            aria-label={t("navigation.new_entry")}
          >
            <Plus className={cn("h-6 w-6 transition-transform duration-200", fabOpen && "rotate-45")} />
          </button>
        </div>

        {navItems.slice(2).map(({ key, route, label, Icon }) => {
          const active = isActive(route);
          return (
            <button
              key={key}
              onClick={() => navigate(route)}
              className={cn(
                "relative flex h-full flex-col items-center justify-center gap-1 px-1",
                active ? "text-brand-600" : "text-[#3B608F]"
              )}
            >
              {active && (
                <span className="absolute left-1/2 top-1 h-1 w-8 -translate-x-1/2 rounded-full bg-brand-600" />
              )}
              <Icon className="h-5 w-5" />
              <span className={cn("text-[11px] leading-[16.5px]", active && "font-semibold")}>
                {label}
              </span>
            </button>
          );
        })}
      </div>

      <ProgressQuickEntryModal
        isOpen={progressModalOpen}
        onClose={() => setProgressModalOpen(false)}
        initialProjectId={currentProjectId ?? undefined}
      />
    </div>
  );
};

export default BottomNavigation;
